import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { Calendar, Clock, User, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import '../admin/LeaveApprovalView.css';

const ManagerLeaveApprovalView = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchLeaveRequests();
  }, [filter]);

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      let response;
      if (filter === 'pending') {
        response = await leaveAPI.getPendingRequests();
      } else {
        response = await leaveAPI.getAllRequests();
      }
      
      let requests = response.data.leaveRequests || [];
      
      // Filter based on selected filter
      if (filter === 'approved') {
        requests = requests.filter(r => 
          r.status === 'MANAGER_APPROVED' || r.status === 'ADMIN_APPROVED'
        );
      } else if (filter === 'rejected') {
        requests = requests.filter(r => r.status === 'REJECTED');
      }
      
      setLeaveRequests(requests);
    } catch (error) {
      toast.error('Failed to fetch leave requests');
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    const comment = prompt('Enter approval comment (optional):');
    if (comment === null) return; // User cancelled
    
    try {
      setActionLoading(id);
      await leaveAPI.managerApprove(id, comment || '');
      toast.success('Leave request approved successfully. Sent to admin for final approval.');
      
      // Small delay to ensure backend has processed, then fetch updated data
      setTimeout(() => {
        fetchLeaveRequests();
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    const comment = prompt('Enter rejection reason:');
    if (!comment) {
      toast.warning('Rejection reason is required');
      return;
    }
    
    try {
      setActionLoading(id);
      await leaveAPI.managerReject(id, comment);
      toast.success('Leave request rejected');
      
      // Small delay to ensure backend has processed, then fetch updated data
      setTimeout(() => {
        fetchLeaveRequests();
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { icon: AlertCircle, class: 'status-pending', text: 'Pending' },
      'MANAGER_APPROVED': { icon: CheckCircle, class: 'status-manager-approved', text: 'Approved (Pending Admin)' },
      'ADMIN_APPROVED': { icon: CheckCircle, class: 'status-approved', text: 'Fully Approved' },
      'REJECTED': { icon: XCircle, class: 'status-rejected', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig['PENDING'];
    const Icon = config.icon;
    
    return (
      <span className={`status-badge ${config.class}`}>
        <Icon size={14} />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  return (
    <div className="leave-approval-container">
      <div className="leave-approval-header">
        <h2>Team Leave Requests</h2>
        <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={filter === 'approved' ? 'active' : ''}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            className={filter === 'rejected' ? 'active' : ''}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading leave requests...</div>
      ) : leaveRequests.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>No leave requests found</p>
        </div>
      ) : (
        <div className="leave-requests-grid">
          {leaveRequests.map((request) => (
            <div key={request.id} className="leave-request-card">
              <div className="card-header">
                <div className="employee-info">
                  <User size={20} />
                  <div>
                    <h3>{request.employee_name}</h3>
                    <p className="department">{request.department}</p>
                  </div>
                </div>
                {getStatusBadge(request.status)}
              </div>

              <div className="card-body">
                <div className="leave-info-row">
                  <div className="info-item">
                    <FileText size={16} />
                    <span className="label">Type:</span>
                    <span className="value">{request.leave_type}</span>
                  </div>
                  <div className="info-item">
                    <Clock size={16} />
                    <span className="label">Duration:</span>
                    <span className="value">
                      {calculateDays(request.start_date, request.end_date)} days
                    </span>
                  </div>
                </div>

                <div className="leave-info-row">
                  <div className="info-item">
                    <Calendar size={16} />
                    <span className="label">From:</span>
                    <span className="value">{formatDate(request.start_date)}</span>
                  </div>
                  <div className="info-item">
                    <Calendar size={16} />
                    <span className="label">To:</span>
                    <span className="value">{formatDate(request.end_date)}</span>
                  </div>
                </div>

                {request.reason && (
                  <div className="reason-box">
                    <strong>Reason:</strong>
                    <p>{request.reason}</p>
                  </div>
                )}

                {request.manager_comment && (
                  <div className="comment-box manager-comment">
                    <strong>Manager Comment:</strong>
                    <p>{request.manager_comment}</p>
                  </div>
                )}

                {request.admin_comment && (
                  <div className="comment-box admin-comment">
                    <strong>Admin Comment:</strong>
                    <p>{request.admin_comment}</p>
                  </div>
                )}

                <div className="applied-date">
                  Applied on: {formatDate(request.applied_at)}
                </div>
              </div>

              {request.status === 'PENDING' && (
                <div className="card-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(request.id)}
                    disabled={actionLoading === request.id}
                  >
                    <CheckCircle size={16} />
                    {actionLoading === request.id ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleReject(request.id)}
                    disabled={actionLoading === request.id}
                  >
                    <XCircle size={16} />
                    Reject
                  </button>
                </div>
              )}

              {(request.status === 'MANAGER_APPROVED' || request.status === 'ADMIN_APPROVED') && (
                <div className="card-actions">
                  <div className="info-message">
                    {request.status === 'MANAGER_APPROVED' 
                      ? 'Waiting for admin approval' 
                      : 'Leave approved by admin'}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManagerLeaveApprovalView;
